const PLANET_COLORS = {
  Sun: "#B8451C", Moon: "#27AE60", Mars: "#C62828", Mercury: "#1565C0",
  Jupiter: "#E8A317", Venus: "#8E44AD", Saturn: "#37474F", Rahu: "#6D4C41", Ketu: "#6D4C41",
};

function getSymbols(p) {
  let s = "";
  if (p.is_retrograde) s += "*";
  if (p.is_exalted) s += "\u2191";
  if (p.is_debilitated) s += "\u2193";
  if (p.is_combust) s += "^";
  if (p.is_vargottama) s += "\u25A1";
  return s;
}

// Sign number at outer edge, planets centered in house — prevents overlap
const LAYOUT = {
  1:  { sx: 210, sy: 35,  cx: 210, cy: 110, dy: 18, fs: 12 },
  2:  { sx: 75,  sy: 28,  cx: 112, cy: 65,  dy: 15, fs: 10 },
  3:  { sx: 22,  sy: 62,  cx: 52,  cy: 112, dy: 15, fs: 10 },
  4:  { sx: 32,  sy: 210, cx: 115, cy: 210, dy: 18, fs: 12 },
  5:  { sx: 22,  sy: 358, cx: 52,  cy: 308, dy: 15, fs: 10 },
  6:  { sx: 75,  sy: 392, cx: 112, cy: 358, dy: 15, fs: 10 },
  7:  { sx: 210, sy: 388, cx: 210, cy: 312, dy: 18, fs: 12 },
  8:  { sx: 345, sy: 392, cx: 308, cy: 358, dy: 15, fs: 10 },
  9:  { sx: 398, sy: 358, cx: 368, cy: 308, dy: 15, fs: 10 },
  10: { sx: 388, sy: 210, cx: 305, cy: 210, dy: 18, fs: 12 },
  11: { sx: 398, sy: 62,  cx: 368, cy: 112, dy: 15, fs: 10 },
  12: { sx: 345, sy: 28,  cx: 308, cy: 65,  dy: 15, fs: 10 },
};

export default function NorthIndianChart({ houses }) {
  if (!houses) return null;
  return (
    <div data-testid="kundli-chart" className="rounded-xl overflow-hidden border-2 border-amber-600/40">
      <svg viewBox="0 0 420 462" className="w-full max-w-[440px] mx-auto">
        <rect width="420" height="420" fill="#FDF6E8" />
        <rect x="4" y="4" width="412" height="412" fill="none" stroke="#C8963E" strokeWidth="2.5" rx="3" />
        <rect x="9" y="9" width="402" height="402" fill="none" stroke="#C8963E" strokeWidth="1" rx="2" />
        <g stroke="#C8963E" strokeWidth="1.5">
          <line x1="10" y1="10" x2="410" y2="410" />
          <line x1="410" y1="10" x2="10" y2="410" />
          <polygon points="210,10 10,210 210,410 410,210" fill="none" />
        </g>
        <polygon points="203,12 217,12 210,24" fill="#C8963E" />

        {Object.entries(houses).map(([num, house]) => {
          const h = parseInt(num);
          const L = LAYOUT[h];
          if (!L) return null;
          const signNum = house.sign?.number;
          const planets = house.planets || [];
          const totalH = Math.max(0, (planets.length - 1) * L.dy);
          const baseY = L.cy - totalH / 2;

          return (
            <g key={num}>
              {/* Sign number — small, muted, at outer edge */}
              <text x={L.sx} y={L.sy} fill="#9C8560" fontSize="11" fontWeight="600"
                textAnchor="middle" dominantBaseline="central"
                fontFamily="'Quicksand', sans-serif" opacity="0.7"
                data-testid={`house-${h}-sign`}>
                {signNum}
              </text>

              {/* Planets with degree + status symbols */}
              {planets.map((p, i) => {
                const color = PLANET_COLORS[p.name] || "#333";
                const sym = getSymbols(p);
                const deg = p.degree_in_sign != null ? String(p.degree_in_sign).padStart(2, "0") + "°" : "";
                const y = baseY + i * L.dy;
                const label = p.abbreviation + (deg ? " " + deg : "") + sym;

                return (
                  <g key={p.name} data-testid={`planet-${p.name.toLowerCase()}`}>
                    {/* Degree in red, offset above-left */}
                    {deg && (
                      <text x={L.cx - 2} y={y - (L.fs * 0.55)} fill="#CC3333"
                        fontSize={L.fs * 0.65} fontWeight="600"
                        textAnchor="end" fontFamily="'JetBrains Mono', monospace">
                        {deg}
                      </text>
                    )}
                    {/* Planet abbreviation + symbols */}
                    <text x={L.cx} y={y} fill={color}
                      fontSize={L.fs} fontWeight="700"
                      textAnchor="middle" dominantBaseline="central"
                      fontFamily="'JetBrains Mono', monospace">
                      {p.abbreviation}{sym}
                    </text>
                  </g>
                );
              })}
            </g>
          );
        })}

        {/* Legend bar — two rows */}
        <rect x="0" y="420" width="420" height="42" fill="#FCDEC0" />
        <line x1="0" y1="420" x2="420" y2="420" stroke="#C8963E" strokeWidth="1" />
        <g fontFamily="'Quicksand', sans-serif" fontWeight="700" fontSize="9">
          <text x="20" y="436" fill="#1a1a1a"><tspan fontWeight="900">*</tspan> Retrograde</text>
          <text x="130" y="436" fill="#1a1a1a"><tspan fontWeight="900">^</tspan> Combust</text>
          <text x="220" y="436" fill="#1a1a1a"><tspan fontWeight="900">{"\u25A1"}</tspan> Vargottama</text>
          <text x="330" y="436" fill="#1a1a1a"><tspan fontWeight="900">{"\u25B2"}</tspan> Lagna</text>
          <text x="20" y="453" fill="#CC3333"><tspan fontWeight="900">{"\u2191"}</tspan> Exalted</text>
          <text x="120" y="453" fill="#CC3333"><tspan fontWeight="900">{"\u2193"}</tspan> Debilitated</text>
          <text x="250" y="453" fill="#CC3333">Red = Degrees</text>
        </g>
      </svg>
    </div>
  );
}
